/**
 * The single source of truth for the locked sidebar (SkyNavDrawer). Mirrors skySign's sidebarConfig:
 * GROUPED NavSection[] with a section header per cluster and a lucide-react icon on every NavItem, so the
 * drawer renders the amber LEFT-BAR + amber text on the active item (driven by SkyProvider brand + size).
 * Every V1 screen appears here, including the DEFERRED-badged ones (honest, not hidden). routeToNavId maps
 * the current pathname to the active nav id so exactly one item carries the amber active bar.
 */
import type { NavSection } from "@skypointcloud/skypoint-ui";
import {
  Activity,
  AlertOctagon,
  BarChart3,
  Building2,
  ClipboardList,
  FileText,
  HeartPulse,
  KeyRound,
  Layers,
  LayoutDashboard,
  Pill,
  ScrollText,
  Server,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";

export const NAV_SECTIONS: NavSection[] = [
  {
    header: "Home",
    items: [{ id: "dashboard", label: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    header: "Care",
    items: [
      { id: "roster", label: "Participants", href: "/roster", icon: Users },
      { id: "vitals", label: "Vitals", href: "/vitals", icon: HeartPulse },
      { id: "encounters", label: "Encounters", href: "/encounters", icon: FileText },
      { id: "idt", label: "Care Plans & IDT", href: "/idt", icon: ClipboardList },
      { id: "medications", label: "Medications", href: "/medications", icon: Pill },
    ],
  },
  {
    header: "Population & Quality",
    items: [
      { id: "population", label: "Population Health", href: "/population", icon: BarChart3 },
      { id: "census", label: "Census", href: "/census", icon: Building2 },
      { id: "quality", label: "Quality", href: "/quality", icon: ShieldCheck },
      { id: "care-gaps", label: "Care Gaps", href: "/care-gaps", icon: AlertOctagon },
      { id: "utilization", label: "Avoidable Utilization", href: "/utilization", icon: Activity },
    ],
  },
  {
    header: "Data",
    items: [{ id: "data-health", label: "Data Health & Matrix", href: "/data-health", icon: Layers }],
  },
  {
    // Administration (M2 W5b). RBAC-gated: deriveNavigation maps each id to its manage/view
    // permission, so non-admins never see this group and the whole section drops for them.
    header: "Administration",
    items: [
      { id: "admin-users", label: "Users", href: "/admin/users", icon: UserCog },
      { id: "admin-roles", label: "Roles", href: "/admin/roles", icon: KeyRound },
      { id: "admin-tenants", label: "Tenants", href: "/admin/tenants", icon: Building2 },
      { id: "admin-instances", label: "Instances", href: "/admin/instances", icon: Server },
      { id: "admin-audit-log", label: "Audit Log", href: "/admin/audit-log", icon: ScrollText },
    ],
  },
];

/** Map a router pathname to the active nav id (one active item at a time). */
export function routeToNavId(pathname: string): string {
  if (pathname === "/") return "dashboard";
  if (pathname.startsWith("/roster") || pathname.startsWith("/member")) return "roster";
  // /admin/<section> -> "admin-<section>" (e.g. /admin/audit-log -> "admin-audit-log").
  if (pathname.startsWith("/admin/")) {
    const section = pathname.split("/")[2] ?? "";
    return section ? `admin-${section}` : "dashboard";
  }
  return pathname.slice(1).split("/")[0];
}

/**
 * Filter the full NAV_SECTIONS down to the RBAC-allowed items (M2 W3). `allowedIds`
 * comes from `/api/v1/me`.allowedNav (server-derived from the principal's permissions).
 * When `allowedIds` is null/undefined (session not loaded), the full nav is returned —
 * gating only tightens once the session resolves. Sections with no visible items drop.
 */
export function filterNavSections(
  sections: NavSection[],
  allowedIds: string[] | null | undefined,
): NavSection[] {
  if (!allowedIds) return sections;
  const allowed = new Set(allowedIds);
  return sections
    .map((s) => ({ ...s, items: s.items.filter((i) => allowed.has(i.id)) }))
    .filter((s) => s.items.length > 0);
}
